import type {
  Budget,
  versionInfo,
  VersionTotals,
} from "../types/budget/budget";
import type { BudgetServiceValuePhase } from "../types/budget/budget-service-v2";
import type {
  BudgetTotalsResponse,
  ServiceWriteTotalsAttachment,
  ServiceWriteTotalsSlice,
} from "../types/budget/budget-totals";
import { resolveServiceValuePhase } from "./budget-service-writes";

/** Totals tree fields the server owns when `USE_SERVER_TOTALS` is on (FE-P4-02). */
export const CLIENT_TOTALS_TREE_KEYS = [
  "budgetValuesTotal",
  "negociationValuesTotal",
  "closingValuesTotal",
] as const;

export type ClientTotalsTreeKey = (typeof CLIENT_TOTALS_TREE_KEYS)[number];

/**
 * FE-P4-02: omit client-assembled totals trees from a v1 PUT payload.
 *
 * When `USE_SERVER_TOTALS` is on, BE-P4-06 ignores these fields and recomputes.
 * Stripping them keeps save payloads lean and avoids implying client ownership.
 * Local trees remain on the editor document for display-only optimistic UX.
 */
export function stripTotalsTreesFromBudgetPayload<
  T extends Record<string, unknown>
>(budget: T): Omit<T, ClientTotalsTreeKey> {
  const {
    budgetValuesTotal: _budgetValuesTotal,
    negociationValuesTotal: _negociationValuesTotal,
    closingValuesTotal: _closingValuesTotal,
    ...rest
  } = budget as T & Partial<Record<ClientTotalsTreeKey, unknown>>;

  return rest as Omit<T, ClientTotalsTreeKey>;
}

/**
 * After a v1 PUT with `USE_SERVER_TOTALS`, the API body omits (or returns stale)
 * totals trees because the client stripped them from the payload. Keep the
 * editor's optimistic trees until `GET /totals` / write-response slices apply.
 */
export function preserveLocalTotalsTrees(
  apiBudget: Budget,
  localBudget: Budget
): Budget {
  if (!apiBudget || !localBudget) return apiBudget;
  return {
    ...apiBudget,
    budgetValuesTotal:
      localBudget.budgetValuesTotal ?? apiBudget.budgetValuesTotal,
    negociationValuesTotal:
      localBudget.negociationValuesTotal ?? apiBudget.negociationValuesTotal,
    closingValuesTotal:
      localBudget.closingValuesTotal ?? apiBudget.closingValuesTotal,
    totalsVersion: localBudget.totalsVersion ?? apiBudget.totalsVersion,
  };
}

/**
 * True when the local `totalsVersion` is behind the server (FE-P4-01 stale detection).
 * Missing local version is treated as stale whenever a remote version is present.
 */
export function isTotalsVersionStale(
  localVersion: number | undefined | null,
  remoteVersion: number | undefined | null
): boolean {
  if (remoteVersion == null || Number.isNaN(Number(remoteVersion))) {
    return false;
  }
  if (localVersion == null || Number.isNaN(Number(localVersion))) {
    return true;
  }
  return Number(remoteVersion) > Number(localVersion);
}

/**
 * Pull `{ totalsVersion, totals }` off a v2 write response when present.
 */
export function pickWriteTotalsAttachment(
  response: unknown
): ServiceWriteTotalsAttachment | undefined {
  if (!response || typeof response !== "object") return undefined;
  const body = response as Record<string, unknown>;
  if (typeof body.totalsVersion !== "number" || !Array.isArray(body.totals)) {
    return undefined;
  }
  return {
    totalsVersion: body.totalsVersion,
    totals: body.totals as ServiceWriteTotalsSlice[],
  };
}

/**
 * Maps a GET /totals response into a single-slice attachment for apply helpers.
 */
export function totalsResponseToAttachment(
  response: BudgetTotalsResponse
): ServiceWriteTotalsAttachment {
  return {
    totalsVersion: response.totalsVersion,
    totals: [
      {
        phase: response.phase,
        negotiationVersion: response.negotiationVersion ?? null,
        totals: response.totals,
        computedAt: response.computedAt,
        source: response.source,
      },
    ],
  };
}

function applySliceToBudget(
  budget: Budget,
  slice: ServiceWriteTotalsSlice
): Budget {
  const phase = slice.phase;
  const tree = slice.totals as VersionTotals;

  if (phase === "budget") {
    return { ...budget, budgetValuesTotal: tree };
  }

  if (phase === "closing") {
    return { ...budget, closingValuesTotal: tree };
  }

  // negotiation — index = negotiationVersion - 1
  const version =
    slice.negotiationVersion != null && slice.negotiationVersion > 0
      ? slice.negotiationVersion
      : 1;
  const index = version - 1;
  const existing = [...(budget.negociationValuesTotal ?? [])];
  while (existing.length <= index) {
    existing.push({
      byCalculationType: { budgetTotals: {} },
      byItemType: { SUM: { totalInBRL: 0, totalInCurrency: 0 } },
    } as VersionTotals);
  }
  existing[index] = tree;
  return { ...budget, negociationValuesTotal: existing };
}

/**
 * Merges server totals slices into the editor/list budget document (FE-P4-01).
 * Only replaces the affected phase trees; other phases stay intact.
 * Sets `totalsVersion` when provided on the attachment.
 */
export function applyServerTotalsSlices(
  budget: Budget,
  attachment: Pick<ServiceWriteTotalsAttachment, "totals" | "totalsVersion">
): Budget {
  let next = budget;
  for (const slice of attachment.totals ?? []) {
    if (!slice?.phase || !slice.totals) continue;
    next = applySliceToBudget(next, slice);
  }
  if (typeof attachment.totalsVersion === "number") {
    next = { ...next, totalsVersion: attachment.totalsVersion };
  }
  return next;
}

/**
 * Active editor phase for `GET /v2/budget/:id/totals` (same mapping as batch writes).
 */
export function resolveTotalsPhaseFromVersionInfo(
  versionInfo: Pick<versionInfo, "version" | "status">
): {
  phase: BudgetServiceValuePhase;
  negotiationVersion?: number;
} {
  const resolved = resolveServiceValuePhase(versionInfo);
  return {
    phase: resolved.valuePhase,
    negotiationVersion: resolved.negotiationVersion,
  };
}
